import React, { useState, useEffect } from "react";
import { Spin, Upload, Button, Row, Col, Modal } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import XLSX from "xlsx";
import { useSelector } from "react-redux";
import { omit } from "lodash";
import { ReactSVG } from "react-svg";
import { CloseOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import moment from "moment";
import { useDispatch } from "react-redux";

import "./Order.scss";
import CsvMapperForm from "./CsvMapper";
import { getTranslation } from "../../../utils/helper";
import { UPLOADED_TIME_ORDER, DATE_FORMAT_QUERY } from "../../../constants";
import DefaultContactLocation from "../Orders/DefaultContactLocation";

import { doCreateMutiOrders } from "../../../modules/order/stores/thunk";
import { doDetailRoutePlan } from "../../../modules/routePlan/stores/thunk";

const UploadForm = ({ isFetching, query, doUploadOrders, setMatch, hideDocs, routePlan, history }) => {
  const dispatch = useDispatch();
  const { isFetching: isFetchingOrder, action, status } = useSelector(state => state.order);
  const user = useSelector(state => state.auth.user);
  const template = user.uploadOrderTemplate || {};
  const setting = useSelector(state => state.setting.setting) || user?.company;
  const routePlanType = query?.routePlanType || setting?.routePlanType || "PDP";

  const [state, setState] = useState({
    data: [],
    visibleModal: false,
    contacts: [],
  });

  const [isClosedDocs, setIsClosedDocs] = useState(false);

  useEffect(() => {
    setIsClosedDocs(!!Cookies.get("ClosedDocs"));
  }, []);

  const handleFileChange = e => {
    setState({ ...state, loading: true });
    const file = e.file.originFileObj;
    const reader = new FileReader();
    const extension = file.name.split(".").pop().toLowerCase();
    let type;
    switch (extension) {
      case "csv":
        reader.readAsText(file, "UTF-8");
        type = "string";
        break;
      case "xlsx":
        reader.readAsBinaryString(file);
        type = "binary";
        break;
      case "xls":
        reader.readAsBinaryString(file);
        type = "binary";
        break;
      case "xltx":
        reader.readAsBinaryString(file);
        type = "binary";
        break;
    }
    reader.onload = e => {
      const bstr = e.target.result;
      const wb = XLSX.read(bstr, { type, raw: true, cellDates: true });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { raw: false, defval: "" });
      setState({ ...state, file, data, loading: false });
      setMatch && setMatch(true);
    };
  };

  const handleComplete = async template => {
    const formData = new FormData();
    formData.append("file", state.file);
    if (routePlanType === "PDP") {
      formData.append("pickupName", template["Pickup Name"]?.value || "");
      formData.append("pickupPhone", template["Pickup Phone"]?.value || "");
      formData.append("pickupEmail", template["Pickup Email"]?.value || "");
      if (
        !!template["Pickup Latitude"]?.value &&
        !!template["Pickup Longitude"]?.value &&
        !template["Pickup Address"]?.value
      ) {
        formData.append(
          "pickupAddress",
          `${template["Pickup Latitude"]?.value},${template["Pickup Longitude"]?.value}`,
        );
      } else {
        formData.append("pickupAddress", template["Pickup Address"]?.value || "");
      }
      formData.append("pickupLat", template["Pickup Latitude"]?.value || "");
      formData.append("pickupLng", template["Pickup Longitude"]?.value || "");
      formData.append("timeStart", template["Pickup From"]?.value || "");
      formData.append("timeEnd", template["Pickup To"]?.value || "");
      formData.append("pickupDuration", template["Pickup Duration"]?.value || "");
      formData.append("pickupNotes", template["Pickup Notes"]?.value || "");
    }
    formData.append("deliveryName", template["Delivery Name"]?.value || "");
    formData.append("deliveryPhone", template["Delivery Phone"]?.value || "");
    formData.append("deliveryEmail", template["Delivery Email"]?.value || "");
    if (
      !!template["Delivery Latitude"]?.value &&
      !!template["Delivery Longitude"]?.value &&
      !template["Delivery Address"]?.value
    ) {
      formData.append(
        "deliveryAddress",
        `${template["Delivery Latitude"]?.value},${template["Delivery Longitude"]?.value}`,
      );
    } else {
      formData.append("deliveryAddress", template["Delivery Address"]?.value || "");
    }
    formData.append("deliveryLat", template["Delivery Latitude"]?.value || "");
    formData.append("deliveryLng", template["Delivery Longitude"]?.value || "");
    formData.append("deliveryTimeStart", template["Delivery From"]?.value || "");
    formData.append("deliveryTimeEnd", template["Delivery To"]?.value || "");
    formData.append("deliveryDuration", template["Delivery Duration"]?.value || "");
    formData.append("deliveryNotes", template["Delivery Notes"]?.value || "");
    formData.append("orderRef", template["Order Ref"]?.value || "");
    formData.append("deliveryDate", template["Date"]?.value || "");
    formData.append("group", template["User Group"]?.value || "");
    formData.append("load", template["Load"]?.value || "");
    formData.append("types", template["Types"]?.value || "");
    formData.append("notes", template["Notes"]?.value || "");
    formData.append("fields", JSON.stringify(template["Fields"] || {}));
    formData.append("routePlanType", routePlanType);

    // Store uploadedTime
    const uploadedTime = new Date().getTime();
    formData.append("uploadedTime", uploadedTime);
    Cookies.set(UPLOADED_TIME_ORDER, uploadedTime);

    await doUploadOrders(formData);
  };

  const handleCancel = () => {
    setState({ ...state, data: [] });
    setMatch && setMatch(false);
  };

  const handleCloseDocs = e => {
    e.preventDefault();
    Cookies.set("ClosedDocs", true);
    setIsClosedDocs(true);
  };

  const handleOpenDocs = () => {
    Cookies.remove("ClosedDocs");
    setIsClosedDocs(false);
  };

  const getTemplate = template => {
    let templateT = {
      "Order Ref": {
        value: template["Order Ref"],
        require: false,
        info: "Unique order reference number e.g. INV-ABC123.",
      },
      Date: {
        value: query.deliveryDate || template["Date"],
        require: true,
        info: "Date that this order will be picked up and delivered.",
      },
      "Pickup Name": {
        value: template["Pickup Name"],
        require: false,
        info: 'Name of the customer you are picking up this order from. If you are picking up this order from a warehouse, use "Warehouse" as the pickup name.',
      },
      "Pickup Phone": {
        value: template["Pickup Phone"],
        require: false,
        info: "Phone number of the customer you are picking up this order from. Remember to include the country code and plus sign e.g. +17789123456 for US/Canada numbers or +6593456789 for Singapore numbers.",
      },
      "Pickup Email": {
        value: template["Pickup Email"],
        require: false,
        info: "Email of the customer you are picking up this order from.",
      },
      "Pickup Address": {
        value: template["Pickup Address"],
        require: routePlanType === "PDP",
        info: "Address of the customer you are picking up this order from.",
      },
      "Pickup Latitude": {
        value: template["Pickup Latitude"],
        require: false,
        info: "Geographic coordinates indicating the precise location for order pickup.",
      },
      "Pickup Longitude": {
        value: template["Pickup Longitude"],
        require: false,
        info: "Geographic coordinates indicating the precise location for order pickup.",
      },
      "Pickup From": {
        value: template["Pickup From"],
        require: false,
        info: "The earliest time you need to pick up this order.",
      },
      "Pickup To": {
        value: template["Pickup To"],
        require: false,
        info: "The latest time you need to pick up this order.",
      },
      "Pickup Duration": {
        value: template["Pickup Duration"],
        require: false,
        info: "The time spent (in minutes) at the pickup location.",
      },
      "Pickup Notes": {
        value: template["Pickup Notes"],
        require: false,
        info: 'Any information that can help the driver successfully make his delivery e.g. "Leave the package on the porch".',
      },
      "Delivery Name": {
        value: template["Delivery Name"],
        require: false,
        info: "Name of the customer you are delivering this order to.",
      },
      "Delivery Phone": {
        value: template["Delivery Phone"],
        require: false,
        info: "Phone number of the customer you are delivering this order to. If you've set up customer text notifications, the customer will receive text messages updating him on the status of this order. Remember to include the country code and plus sign e.g. +17789123456 for US/Canada numbers or +6593456789 for Singapore numbers.",
      },
      "Delivery Email": {
        value: template["Delivery Email"],
        require: false,
        info: "Email of the customer you are picking up this order from. If you've set up customer email notifications, the customer will receive emails updating him on the status of this order.",
      },
      "Delivery Address": {
        value: template["Delivery Address"],
        require: true,
        info: "Address of the customer you are delivering this order to.",
      },
      "Delivery Latitude": {
        value: template["Delivery Latitude"],
        require: false,
        info: "Geographic coordinates specifying the exact location for delivering the order.",
      },
      "Delivery Longitude": {
        value: template["Delivery Longitude"],
        require: false,
        info: "Geographic coordinates specifying the exact location for delivering the order.",
      },
      "Delivery From": {
        value: template["Delivery From"],
        require: false,
        info: "The earliest time you need to deliver this order.",
      },
      "Delivery To": {
        value: template["Delivery To"],
        require: false,
        info: "The latest time you need to deliver this order.",
      },
      "Delivery Duration": {
        value: template["Delivery Duration"],
        require: false,
        info: "The time spent (in minutes) at the delivery location.",
      },
      "Delivery Notes": {
        value: template["Delivery Notes"],
        require: false,
        info: 'Any information that can help the driver successfully make his delivery e.g. "Leave the package on the porch".',
      },
      "User Group": {
        value: template["User Group"],
        require: false,
        info: "Groups of users that can log in and track the status of this order.",
      },
      Load: {
        value: template["Load"],
        require: false,
        info: "How big (e.g. how many individual packages) your order is. Used together with driver capacities to determine how many stops can be done in a single route.",
      },
      Types: {
        value: template["Types"],
        require: false,
        info: "For advanced users only. Use this to match specific orders with drivers that are equipped to do those orders e.g. a VIP driver can pickup and deliver VIP orders.",
      },
    };

    if (routePlanType !== "PDP") {
      templateT = omit(templateT, [
        "Pickup Name",
        "Pickup Address",
        "Pickup Latitude",
        "Pickup Longitude",
        "Pickup Phone",
        "Pickup Email",
        "Pickup Duration",
        "Pickup From",
        "Pickup To",
        "Pickup Notes",
      ]);
    }

    return templateT;
  };

  const deliveryDateQuery = moment(routePlan?.deliveryDate).utc().format(DATE_FORMAT_QUERY);

  useEffect(() => {
    if (action === "create-muti" && status === "succeeded") {
      dispatch(doDetailRoutePlan(routePlan?._id));
    }
  }, [isFetchingOrder]);

  const handleClickAddSingleOrder = () => {
    history.push(
      `/orders/new?redirectUrl=${window.location.pathname}&deliveryDate=${deliveryDateQuery}&routePlanType=${routePlan?.type}&routePlanId=${routePlan?._id}`,
    );
  };

  const handleClickAddFromContactList = () => {
    setState({
      ...state,
      visibleModal: true,
      contacts: [],
    });
  };

  const handleSubmitSelectContact = () => {
    const params = [];
    state.contacts.forEach(contact => {
      params.push({
        deliveryName: contact.name,
        deliveryPhone: contact.phone,
        deliveryTimeStart: contact.startTime,
        deliveryTimeEnd: contact.endTime,
        deliveryDuration: contact.duration,
        deliveryAddress: contact.address,
        deliveryLat: contact.lat,
        deliveryLng: contact.lng,
        requiredSkills: contact.types,
        deliveryNotes: contact.notes,
        load: contact.load,
        fields: contact.fields,
        routePlan: routePlan?._id,
        deliveryDate: deliveryDateQuery,
        routePlanType: "VRP",
      });
    });
    dispatch(doCreateMutiOrders(params));
    handleCancelSelectContact();
    Cookies.set("PreAction", "AddOrdersFromContactList");
  };

  const handleCancelSelectContact = () => {
    setState({
      ...state,
      visibleModal: false,
      contacts: [],
    });
  };

  return (
    <Spin spinning={isFetching || !!state.loading}>
      <div className="UploadForm">
        {state.data && state.data.length === 0 && (
          <div className="UploadOrders">
            <div className="FormContent">
              <Upload.Dragger
                multiple={false}
                accept=".csv, .xls, .xltx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                customRequest={() => null}
                showUploadList={false}
                onChange={e => handleFileChange(e)}>
                <p className="upload-text">Drag and drop to upload or</p>
                <p className="upload-drag-icon">
                  Browse
                  <UploadOutlined />
                </p>
                <p className="upload-supported">(csv and xls supported)</p>
              </Upload.Dragger>
              <div className="bottom-text">
                <div className="download-text">
                  <div>{getTranslation(setting, "Common", "Download Sample File")}: </div>
                  <a target="_blank" href="/files/upload_orders.csv" rel="noopener noreferrer">
                    sample.csv
                  </a>
                </div>
                {routePlan && (
                  <>
                    <div className="NoOrdersYet">
                      <ReactSVG className="icon" src="/icons/inbox.svg" />
                      No orders yet.
                    </div>
                    <div className="AddNewOrders">
                      <Button
                        size="large"
                        className="AddSingleOrder"
                        type="primary"
                        onClick={handleClickAddSingleOrder}>
                        {getTranslation(setting, "Orders", "Add Single Order")}
                        <ReactSVG className="icon" src="/icons/add.svg" />
                      </Button>
                      {routePlan.type === "VRP" && (
                        <Button
                          size="large"
                          type="primary"
                          className="AddFromContactList"
                          onClick={handleClickAddFromContactList}>
                          {getTranslation(setting, "Orders", "Add From Contact List")}
                          <ReactSVG className="icon" src="/icons/note_add.svg" />
                        </Button>
                      )}
                    </div>
                  </>
                )}
                {isClosedDocs && (
                  <div className="download-text">
                    <div>{getTranslation(setting, "Common", "Need help")}? </div>
                    <Button onClick={handleOpenDocs}>
                      {getTranslation(setting, "Common", "View Demo Video")}
                      <ReactSVG className="icon" src="/icons/demo-video.svg" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {state.data && state.data.length === 0 && !isClosedDocs && !hideDocs && (
          <div className="NewParcelizer">
            <Row gutter={24}>
              <Col span={8}>
                <div className="ItemLeft">
                  <div className="TopSection">
                    <div className="Title">New to Parcelizer?</div>
                    <div className="Desc1">
                      Watch this 5 min video to learn how to upload your spreadsheet into Parcelizer.
                    </div>
                  </div>
                  <div className="BottomSection">
                    <div className="Desc2">Still need help? Talk to a support engineer or read our documentation.</div>
                    <a
                      href="https://intercom.help/parcelizer/en/articles/5974506-how-to-add-orders-bulk-upload"
                      target="_blank"
                      rel="noreferrer"
                      className="Documentation">
                      <div className="DocumentationLeft">
                        <div className="DocumentationTitle">
                          <ReactSVG className="icon" src="/icons/docs.svg" />
                          <span>How to Add Orders (Bulk Upload) </span>
                        </div>
                        <div className="DocumentationDesc">
                          Adding orders to your route plan by uploading a CSV file
                        </div>
                      </div>
                      <div className="DocumentationRight">
                        <img src="/icons/arrow-forward.svg" alt="" />
                      </div>
                    </a>
                    <Link className="close" to="#" onClick={handleCloseDocs}>
                      {getTranslation(setting, "Orders", "Do not show this again")}
                      <CloseOutlined />
                    </Link>
                  </div>
                </div>
              </Col>
              <Col span={2}></Col>
              <Col span={14}>
                <div className="ItemRight">
                  <div
                    style={{
                      position: "relative",
                      paddingBottom: "62.5%",
                      height: 0,
                    }}>
                    <iframe
                      title="Upload Form"
                      src="https://app.supademo.com/embed/QmhpQxE6uKs_PROm4hVx6"
                      frameBorder="0"
                      webkitallowfullscreen="true"
                      mozallowfullscreen="true"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}></iframe>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}
        {state.data && state.data.length > 0 && (
          <div className="UploadOrdersMapping">
            <CsvMapperForm
              isFetching={isFetching}
              template={getTemplate(template)}
              allTemplate={template}
              data={state.data}
              onCancel={handleCancel}
              onComplete={handleComplete}
            />
          </div>
        )}
        <Modal open={state.visibleModal} onCancel={handleCancelSelectContact} footer={[]} width={992}>
          <DefaultContactLocation
            state={state}
            setState={setState}
            handleCancel={handleCancelSelectContact}
            handleSubmit={handleSubmitSelectContact}
            title="Add Orders from Contact List"
            mutiSelect={true}
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default UploadForm;
